/**
 * Shell-level patient flow listener (NEW-WF-06; research sched-flow SF-05,
 * SF-06, SF-07, SF-10). Mounted once in the shell, so it works on every
 * page (the schedule, the chart, the inbox), not only while the Light Board
 * is open.
 *
 * The server decides who gets a ping (`GET /v1/schedule/flow/notifications`:
 * the user's subscription of providers x rooms x events, never the person
 * who pressed the button, escalations for subscribers and office managers).
 * This listener wakes on the live `flow_notification` event and every 30 s
 * (escalations have no event of their own), shows a toast with one action
 * (Seat, Checkout, On my way, Open) and plays the chime the user asked for.
 * Each poll also measures the server clock, so every flow timer on screen
 * uses the server's time (SF-04).
 *
 * The bell keeps a PHI-free line ("Ready for Dr, Op 3"); the patient label
 * ("Jenna T.") appears only in the transient toast.
 */
import { useNavigate } from '@tanstack/react-router';
import type { FlowNotification } from '@velora/contracts/flow';
import { useCallback, useEffect, useRef } from 'react';
import { chime } from '../../modules/lightboard/alerts.js';
import { sendFrame } from '../../modules/lightboard/api.js';
import { observeServerTime } from '../../modules/lightboard/clock.js';
import { flowNotifications, seatAppointment } from '../../modules/schedule/data/flowApi.js';
import { clientFor } from '../api.js';
import { useLive, useLiveStatus } from '../live/LiveProvider.js';
import { toast } from '@velora/ui';
import { notify } from './store.js';

/** Poll interval without the live stream; with it, pings arrive by SSE. */
export const FLOW_POLL_MS = 30_000;
/**
 * Poll interval while the live stream is open: new pings arrive by SSE
 * (`flow_notification`); the poll only catches escalations, which are
 * synthesised from elapsed time and have no event of their own.
 */
export const FLOW_POLL_LIVE_MS = 60_000;

const TITLE: Readonly<Record<FlowNotification['kind'], string>> = {
  here: 'Checked in',
  ready: 'Ready to be seated',
  chair: 'Seated',
  checkout: 'Ready for checkout',
  needs_doctor: 'Ready for Dr',
  escalation: 'Ready for Dr is waiting',
};

const ACTION_LABEL: Readonly<Record<FlowNotification['action'], string>> = {
  seat: 'Seat',
  checkout: 'Checkout',
  on_my_way: 'On my way',
  open: 'Open',
};

/** Toast and bell text for one ping (pure; tested). */
export function pingText(n: FlowNotification): {
  title: string;
  description: string;
  bell: string;
  tone: 'info' | 'warning' | 'danger';
} {
  const room = n.operatoryName ? `, ${n.operatoryName}` : '';
  const who = [n.patientLabel, n.providerName, n.actorName ? `from ${n.actorName}` : null]
    .filter(Boolean)
    .join(' · ');
  return {
    title: `${TITLE[n.kind]}${room}`,
    // Ready for Dr carries the hygienist's clinical summary (role test finding 14).
    description: n.detail ? `${who}\n${n.detail}` : who,
    bell: `${TITLE[n.kind]}${room}`,
    tone: n.kind === 'escalation' ? 'danger' : n.kind === 'needs_doctor' ? 'warning' : 'info',
  };
}

/**
 * Pings not shown yet, oldest first. The first read after sign-in only
 * primes the list (what happened before the shell opened is not replayed).
 */
export function freshPings(
  items: readonly FlowNotification[],
  seen: Set<string>,
  primed: boolean,
): FlowNotification[] {
  const out: FlowNotification[] = [];
  for (const n of items) {
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    if (primed) out.push(n);
  }
  return out;
}

export function FlowNotifier() {
  const navigate = useNavigate();
  const seen = useRef(new Set<string>());
  const primed = useRef(false);
  const since = useRef<string | null>(null);
  const busy = useRef(false);

  const act = useCallback(
    async (n: FlowNotification) => {
      const go = (to: string, search: Record<string, string> = {}) =>
        void navigate({ to: to as never, search: search as never });
      if (n.action === 'on_my_way' && n.signalId) {
        try {
          await sendFrame(
            n.locationId,
            { type: 'signal.claim', frameId: crypto.randomUUID(), signalId: n.signalId },
            n.kind === 'escalation' ? 'urgent' : 'high',
          );
          return;
        } catch {
          // No Light Board add-on or the signal is gone: open the board instead.
        }
      }
      if (n.action === 'seat' && n.appointmentId) {
        try {
          await seatAppointment(clientFor(n.locationId), n.appointmentId);
          toast({ title: `${n.patientLabel ?? 'Patient'}: In chair`, tone: 'success' });
          return;
        } catch {
          // Changed meanwhile or no permission: open the board instead.
        }
      }
      if (n.action === 'checkout' && n.appointmentId) {
        go('/frontdesk', { visit: n.appointmentId });
        return;
      }
      go('/schedule', { view: 'board' });
    },
    [navigate],
  );

  const poll = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const sent = Date.now();
      const res = await flowNotifications(clientFor('all'), since.current);
      observeServerTime(res.serverNow, sent, Date.now());
      // Overlap a minute so a ping committed late is not missed; ids dedupe.
      since.current = new Date(Date.parse(res.serverNow) - 60_000).toISOString();
      const fresh = freshPings(res.items, seen.current, primed.current);
      primed.current = true;
      let chimed = false;
      for (const n of fresh) {
        const t = pingText(n);
        toast({
          title: t.title,
          ...(t.description ? { description: t.description } : {}),
          tone: t.tone === 'info' ? 'info' : 'warning',
          action: { label: ACTION_LABEL[n.action], onAction: () => void act(n) },
        });
        notify({ title: t.bell, tone: t.tone, key: `flow-${n.id}`, href: '/schedule?view=board' });
        if (n.chime && !chimed) {
          chime(n.kind === 'escalation' ? 'urgent' : 'normal');
          chimed = true;
        }
      }
    } catch {
      // Signed out, offline or no schedule access: try again on the next tick.
    } finally {
      busy.current = false;
    }
  }, [act]);

  useLive('flow_notification', () => void poll());
  const streaming = useLiveStatus().status === 'open';
  useEffect(() => {
    void poll();
    const t = window.setInterval(() => void poll(), streaming ? FLOW_POLL_LIVE_MS : FLOW_POLL_MS);
    return () => window.clearInterval(t);
  }, [poll, streaming]);
  return null;
}
